import type { FocusEvent, ReactNode } from "react";
import { FormItem } from "../FormLayout/FormItem";
import { ShowFormErrors } from "../FormLayout/ShowFormErrors";
import { TextInput } from "./TextInput";
import type { Dimension, Units } from "../Data/Units";
import { convertFromUnit, convertToUnit } from "../Data/Units";

/**
 * A single freight measurement field (a length/width/height, or a weight) built
 * on `FormItem` + `TextInput`. This is the freight-aware layer that the plain
 * `NumberInput` deliberately isn't:
 *
 *  - **Unit conversion.** The value is stored/emitted in the base (imperial)
 *    unit; it is displayed and parsed in the user's chosen unit.
 *  - **Empty means undefined.** A blank field emits `undefined` (never 0), so a
 *    validator can tell "not entered yet" (→ Required) from a real 0
 *    (→ Should not be zero). The plain `NumberInput` forces a fallback on blur,
 *    which is why this composes `TextInput` instead.
 *  - **Advisory size warning.** An orange "Too long?" / "Too small?" hint from
 *    the min/max thresholds, distinct from a hard validation error. The reason
 *    hangs off the in-field warning triangle — see `sizeWarning`. A caller can
 *    also pass a `warning` it worked out itself, which wins over both.
 *
 * Errors reveal on blur, on submit (`forceValidation` → `ShowFormErrors`), or
 * once the typing pauses — all three are `FormItem`'s doing.
 *
 * `widthPx` is the field's preferred width, not a fixed one. It shrinks when the
 * row is tight, but never below its own title: the label is held on one line, so
 * the flex default (`min-width: auto`, i.e. min-content) floors the field at the
 * title's width. Without the nowrap the floor would collapse to the longest
 * single word ("Pallet"), which is far too narrow to read.
 */

interface DimensionInputProps {
  label: string;
  /** Stored in the base (imperial) unit. */
  value: number | undefined;
  /** Called with the base (imperial) unit, or `undefined` when cleared. */
  onChange: (value: number | undefined) => void;
  units: Units;
  dimension: Dimension;
  errorMessage: ReactNode | undefined;
  forceValidation: boolean;
  /** Preferred field width in px; shrinks down to the label when space is tight. */
  widthPx: number;
  placeholder?: string | undefined;
  onBlur?: ((e: FocusEvent<HTMLInputElement>) => void) | undefined;
  /** Advisory upper threshold, compared against the stored value. */
  maxSizeWarning?: number | undefined;
  /** Advisory lower threshold, compared against the stored value. */
  minSizeWarning?: number | undefined;
  /**
   * A warning the caller worked out, for something this field's own value can't
   * show on its own — an implied density needs the other two axes and the
   * weight. It wins over the threshold warnings above, which are about this
   * field alone, and carries its own explanation (a question bubble), so it
   * leaves the in-field warning mark empty.
   */
  warning?: ReactNode | undefined;
  dataTestId: string;
}

/**
 * The short question goes in the message line; the reason goes on the in-field
 * warning triangle (`FormItem`'s `warningExplanation`), which opens it on focus
 * as well as hover. The reason can't sit inline: these fields are ~72px wide, so
 * a sentence wraps to five lines and pushes into the section below.
 *
 * The wording is fixed rather than derived from the label because the thresholds
 * are — `DimensionsInput` only ever routes `maxSizeWarning` to length and
 * `minSizeWarning` to height.
 */
function sizeWarning(
  value: number | undefined,
  maxSizeWarning: number | undefined,
  minSizeWarning: number | undefined,
): { message: string; explanation: string } | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (maxSizeWarning !== undefined && value > maxSizeWarning) {
    return {
      message: "Too long?",
      explanation:
        "Seems like a high value. Make sure it’s correct before proceeding.",
    };
  }
  if (minSizeWarning !== undefined && value <= minSizeWarning) {
    return {
      message: "Too small?",
      explanation:
        "Seems like a small value. Make sure it’s correct before proceeding.",
    };
  }
  return undefined;
}

export function DimensionInput(props: DimensionInputProps) {
  const displayValue =
    convertToUnit(props.value, props.units, props.dimension)?.toString() ?? "";
  const thresholdWarning = sizeWarning(
    props.value,
    props.maxSizeWarning,
    props.minSizeWarning,
  );
  const warning = props.warning ?? thresholdWarning?.message;
  const warningExplanation =
    props.warning === undefined ? thresholdWarning?.explanation : undefined;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target.value;
    if (input.trim() === "") {
      props.onChange(undefined);
      return;
    }
    const parsed = Number(input);
    if (Number.isNaN(parsed)) {
      return;
    }
    props.onChange(convertFromUnit(parsed, props.units, props.dimension));
  }

  return (
    <div style={{ width: props.widthPx }}>
      <ShowFormErrors show={props.forceValidation}>
        <FormItem
          label={<span className="whitespace-nowrap">{props.label}</span>}
          error={props.errorMessage}
          warning={warning}
          warningExplanation={warningExplanation}
        >
          <TextInput
            type="number"
            value={displayValue}
            onChange={handleChange}
            onBlur={props.onBlur}
            placeholder={props.placeholder}
            width="full"
            data-testid={props.dataTestId}
          />
        </FormItem>
      </ShowFormErrors>
    </div>
  );
}
